import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    Typography,
    Avatar,
    Button,
    styled,
    Tabs,
    Tab,
} from "@mui/material";

import { AiFillBulb } from "react-icons/ai";
import DownloadPDFButton from "./DownloadPdfButton";

/* ✅ Background */
const bgImage =
    "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "60px",
    color: "white",
}));

const TopicCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "25px",
}));

const SectionTitle = styled(Box)(({ theme }) => ({
    textAlign: "center",
    marginTop: "25px",
    marginBottom: "15px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "15px",
    borderRadius: "8px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.5,
}));

const BackButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: "bold",
    padding: "10px 20px",
    borderRadius: "8px",
    "&:hover": { backgroundColor: "#e0b200" },
}));

const TabWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "999px",
    display: "inline-block",
    padding: "4px",
}));

/* ✅ Data: Topics + Solutions */
const topics = [
    {
        title: "Data Types & Control Structures",
        sections: [
            {
                label: "1. Check if a number is prime",
                code: `
public class PrimeCheck {
    public static boolean isPrime(int n) {
        if (n <= 1) return false;
        if (n == 2) return true;
        if (n % 2 == 0) return false;

        for (int i = 3; i * i <= n; i += 2) {
            if (n % i == 0) return false;
        }
        return true;
    }

    public static void main(String[] args) {
        int number = 29;

        if (isPrime(number)) {
            System.out.println(number + " is prime");
        } else {
            System.out.println(number + " is not prime");
        }
    }
}
        `,
            },
            {
                label: "2. Vowel or consonant",
                code: `
public class VowelConsonant {
    public static boolean isVowel(char ch) {
        ch = Character.toLowerCase(ch);
        return ch == 'a' || ch == 'e' || ch == 'i' || ch == 'o' || ch == 'u';
    }

    public static void main(String[] args) {
        char ch = 'E';

        if (!Character.isLetter(ch)) {
            System.out.println(ch + " is not a letter");
        } else if (isVowel(ch)) {
            System.out.println(ch + " is a vowel");
        } else {
            System.out.println(ch + " is a consonant");
        }
    }
}
        `,
            },
            {
                label: "3. Fibonacci sequence up to N",
                code: `
public class Fibonacci {
    public static void printFibonacci(int n) {
        if (n <= 0) return;

        int a = 0;
        int b = 1;

        System.out.print("Fibonacci: ");

        if (n == 1) {
            System.out.println(a);
            return;
        }

        System.out.print(a + " " + b);

        for (int i = 3; i <= n; i++) {
            int c = a + b;
            System.out.print(" " + c);
            a = b;
            b = c;
        }
        System.out.println();
    }

    public static void main(String[] args) {
        int terms = 10;
        printFibonacci(terms);
    }
}
        `,
            },
            {
                label: "4. Largest of three numbers",
                code: `
public class LargestOfThree {
    public static int largest(int a, int b, int c) {
        int max = a;

        if (b > max) max = b;
        if (c > max) max = c;

        return max;
    }

    public static void main(String[] args) {
        int a = 10;
        int b = 25;
        int c = 17;

        int max = largest(a, b, c);
        System.out.println("Largest: " + max);
    }
}
        `,
            },
            {
                label: "5. Switch day of the week",
                code: `
public class DayOfWeekSwitch {
    public static String getDayName(int day) {
        switch (day) {
            case 1: return "Monday";
            case 2: return "Tuesday";
            case 3: return "Wednesday";
            case 4: return "Thursday";
            case 5: return "Friday";
            case 6: return "Saturday";
            case 7: return "Sunday";
            default: return "Invalid day (must be 1-7)";
        }
    }

    public static void main(String[] args) {
        int day = 3;
        System.out.println("Day " + day + ": " + getDayName(day));
    }
}
        `,
            },
        ],
    },

    {
        title: "Methods & Scope",
        sections: [
            {
                label: "1. Factorial of a number",
                code: `
public class Factorial {
    public static long factorial(int n) {
        long result = 1;
        for (int i = 2; i <= n; i++) {
            result *= i;
        }
        return result;
    }

    public static void main(String[] args) {
        int n = 5;
        System.out.println("Factorial of " + n + " = " + factorial(n));
    }
}
        `,
            },
            {
                label: "2. Check if string is palindrome",
                code: `
public class PalindromeCheck {
    public static boolean isPalindrome(String s) {
        int left = 0, right = s.length() - 1;
        while (left < right) {
            if (s.charAt(left) != s.charAt(right)) return false;
            left++;
            right--;
        }
        return true;
    }

    public static void main(String[] args) {
        String text = "level";
        System.out.println(text + " is palindrome? " + isPalindrome(text));
    }
}
        `,
            },
            {
                label: "3. Demonstrate method overloading",
                code: `
public class MethodOverloading {
    public static int add(int a, int b) {
        return a + b;
    }

    public static double add(double a, double b) {
        return a + b;
    }

    public static int add(int a, int b, int c) {
        return a + b + c;
    }

    public static void main(String[] args) {
        System.out.println(add(2, 3));
        System.out.println(add(2.5, 3.7));
        System.out.println(add(1, 2, 3));
    }
}
        `,
            },
            {
                label: "4. Local vs global variables",
                code: `
public class ScopeDemo {
    static int globalValue = 100;

    public static void showScope() {
        int localValue = 50;
        System.out.println("Global: " + globalValue);
        System.out.println("Local: " + localValue);
    }

    public static void main(String[] args) {
        showScope();
        System.out.println("Global still accessible: " + globalValue);
        // localValue is not accessible here
    }
}
        `,
            },
            {
                label: "5. Count vowels in a string",
                code: `
public class VowelCounter {
    public static int countVowels(String s) {
        int count = 0;
        String vowels = "aeiouAEIOU";
        for (int i = 0; i < s.length(); i++) {
            if (vowels.indexOf(s.charAt(i)) != -1) {
                count++;
            }
        }
        return count;
    }

    public static void main(String[] args) {
        String text = "Hello Java Developer";
        System.out.println("Vowel count: " + countVowels(text));
    }
}
        `,
            },
        ],
    },

    {
        title: "OOP (Classes, Inheritance, Polymorphism)",
        sections: [
            {
                label: "1. Car class with attributes and methods",
                code: `
class Car {
    String model;
    int year;

    Car(String model, int year) {
        this.model = model;
        this.year = year;
    }

    void drive() {
        System.out.println(model + " is driving.");
    }
}

public class CarDemo {
    public static void main(String[] args) {
        Car car = new Car("Tesla Model 3", 2023);
        car.drive();
    }
}
        `,
            },
            {
                label: "2. Demonstrate inheritance",
                code: `
class Animal {
    void eat() {
        System.out.println("Animal is eating");
    }
}

class Dog extends Animal {
    void bark() {
        System.out.println("Dog is barking");
    }
}

public class InheritanceDemo {
    public static void main(String[] args) {
        Dog dog = new Dog();
        dog.eat();
        dog.bark();
    }
}
        `,
            },
            {
                label: "3. Implement polymorphism using overriding",
                code: `
class Shape {
    void draw() {
        System.out.println("Drawing a shape");
    }
}

class Circle extends Shape {
    @Override
    void draw() {
        System.out.println("Drawing a circle");
    }
}

class Square extends Shape {
    @Override
    void draw() {
        System.out.println("Drawing a square");
    }
}

public class PolymorphismDemo {
    public static void main(String[] args) {
        Shape s1 = new Circle();
        Shape s2 = new Square();

        s1.draw();
        s2.draw();
    }
}
        `,
            },
            {
                label: "4. Encapsulation with getters/setters",
                code: `
class BankAccount {
    private double balance;

    public double getBalance() {
        return balance;
    }

    public void deposit(double amount) {
        if (amount > 0) balance += amount;
    }

    public void withdraw(double amount) {
        if (amount > 0 && amount <= balance) balance -= amount;
    }
}

public class EncapsulationDemo {
    public static void main(String[] args) {
        BankAccount acc = new BankAccount();
        acc.deposit(1000);
        acc.withdraw(300);
        System.out.println("Balance: " + acc.getBalance());
    }
}
        `,
            },
            {
                label: "5. Simple banking system using classes and objects",
                code: `
class SimpleAccount {
    private String owner;
    private double balance;

    public SimpleAccount(String owner, double initialBalance) {
        this.owner = owner;
        this.balance = initialBalance;
    }

    public void deposit(double amount) {
        if (amount > 0) balance += amount;
    }

    public void withdraw(double amount) {
        if (amount > 0 && amount <= balance) balance -= amount;
    }

    public void printSummary() {
        System.out.println("Owner: " + owner + ", Balance: " + balance);
    }
}

public class BankSystemDemo {
    public static void main(String[] args) {
        SimpleAccount acc = new SimpleAccount("Alice", 500);
        acc.deposit(200);
        acc.withdraw(100);
        acc.printSummary();
    }
}
        `,
            },
        ],
    },

    {
        title: "Arrays & Strings",
        sections: [
            {
                label: "1. Second largest in array",
                code: `
public class SecondLargest {
    public static int secondLargest(int[] arr) {
        if (arr.length < 2) throw new IllegalArgumentException("Need at least 2 elements");

        int max = Integer.MIN_VALUE;
        int second = Integer.MIN_VALUE;

        for (int num : arr) {
            if (num > max) {
                second = max;
                max = num;
            } else if (num > second && num != max) {
                second = num;
            }
        }
        return second;
    }

    public static void main(String[] args) {
        int[] arr = {10, 20, 5, 30, 25};
        System.out.println("Second largest: " + secondLargest(arr));
    }
}
        `,
            },
            {
                label: "2. Reverse string manually",
                code: `
public class ReverseString {
    public static String reverse(String s) {
        StringBuilder sb = new StringBuilder();
        for (int i = s.length() - 1; i >= 0; i--) {
            sb.append(s.charAt(i));
        }
        return sb.toString();
    }

    public static void main(String[] args) {
        String text = "Java";
        System.out.println("Reversed: " + reverse(text));
    }
}
        `,
            },
            {
                label: "3. Bubble sort array",
                code: `
import java.util.Arrays;

public class BubbleSort {
    public static void bubbleSort(int[] arr) {
        int n = arr.length;
        boolean swapped;

        for (int i = 0; i < n - 1; i++) {
            swapped = false;
            for (int j = 0; j < n - 1 - i; j++) {
                if (arr[j] > arr[j + 1]) {
                    int temp = arr[j];
                    arr[j] = arr[j + 1];
                    arr[j + 1] = temp;
                    swapped = true;
                }
            }
            if (!swapped) break;
        }
    }

    public static void main(String[] args) {
        int[] arr = {5, 1, 4, 2, 8};
        bubbleSort(arr);
        System.out.println(Arrays.toString(arr));
    }
}
        `,
            },
            {
                label: "4. Character frequency in string",
                code: `
import java.util.HashMap;
import java.util.Map;

public class CharFrequency {
    public static Map<Character, Integer> countChars(String s) {
        Map<Character, Integer> map = new HashMap<>();
        for (char c : s.toCharArray()) {
            map.put(c, map.getOrDefault(c, 0) + 1);
        }
        return map;
    }

    public static void main(String[] args) {
        String text = "hello world";
        Map<Character, Integer> freq = countChars(text);
        System.out.println(freq);
    }
}
        `,
            },
            {
                label: "5. Merge two arrays into sorted array",
                code: `
import java.util.Arrays;

public class MergeArrays {
    public static int[] mergeAndSort(int[] a, int[] b) {
        int[] merged = new int[a.length + b.length];
        System.arraycopy(a, 0, merged, 0, a.length);
        System.arraycopy(b, 0, merged, a.length, b.length);
        Arrays.sort(merged);
        return merged;
    }

    public static void main(String[] args) {
        int[] a = {3, 1, 5};
        int[] b = {4, 2, 6};
        int[] merged = mergeAndSort(a, b);
        System.out.println(Arrays.toString(merged));
    }
}
        `,
            },
        ],
    },

    {
        title: "Exception Handling",
        sections: [
            {
                label: "1. Handle division by zero",
                code: `
public class DivisionHandling {
    public static void main(String[] args) {
        try {
            int a = 10;
            int b = 0;
            int result = a / b;
            System.out.println(result);
        } catch (ArithmeticException e) {
            System.out.println("Cannot divide by zero");
        }
    }
}
        `,
            },
            {
                label: "2. Custom exception example",
                code: `
class InvalidAgeException extends Exception {
    public InvalidAgeException(String message) {
        super(message);
    }
}

public class CustomExceptionDemo {
    public static void checkAge(int age) throws InvalidAgeException {
        if (age < 18) {
            throw new InvalidAgeException("Age must be 18 or above");
        }
    }

    public static void main(String[] args) {
        try {
            checkAge(16);
        } catch (InvalidAgeException e) {
            System.out.println("Error: " + e.getMessage());
        }
    }
}
        `,
            },
            {
                label: "3. Multiple catch blocks",
                code: `
public class MultipleCatch {
    public static void main(String[] args) {
        try {
            int[] arr = {1, 2, 3};
            int x = arr[5] / 0;
        } catch (ArithmeticException e) {
            System.out.println("Arithmetic error");
        } catch (ArrayIndexOutOfBoundsException e) {
            System.out.println("Array index error");
        } catch (Exception e) {
            System.out.println("General error");
        }
    }
}
        `,
            },
            {
                label: "4. Finally block for cleanup",
                code: `
import java.io.File;
import java.io.FileWriter;
import java.io.IOException;

public class FinallyDemo {
    public static void main(String[] args) {
        FileWriter writer = null;
        try {
            writer = new FileWriter(new File("demo.txt"));
            writer.write("Hello");
        } catch (IOException e) {
            System.out.println("IO Error");
        } finally {
            if (writer != null) {
                try { writer.close(); } catch (IOException ignored) {}
            }
            System.out.println("Cleanup done");
        }
    }
}
        `,
            },
            {
                label: "5. Age validation throws exception",
                code: `
public class AgeValidation {
    public static void validateAge(int age) {
        if (age < 18) {
            throw new IllegalArgumentException("Age must be at least 18");
        }
    }

    public static void main(String[] args) {
        try {
            validateAge(15);
        } catch (IllegalArgumentException e) {
            System.out.println("Validation failed: " + e.getMessage());
        }
    }
}
        `,
            },
        ],
    },

    {
        title: "File Handling",
        sections: [
            {
                label: "1. Create file and write user input",
                code: `
import java.io.FileWriter;
import java.io.IOException;
import java.util.Scanner;

public class FileCreateWrite {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        System.out.print("Enter text: ");
        String text = scanner.nextLine();

        try (FileWriter writer = new FileWriter("user_input.txt")) {
            writer.write(text);
            System.out.println("Written to file.");
        } catch (IOException e) {
            System.out.println("Error writing file");
        }
    }
}
        `,
            },
            {
                label: "2. Count lines in a file",
                code: `
import java.io.BufferedReader;
import java.io.FileReader;
import java.io.IOException;

public class LineCounter {
    public static void main(String[] args) {
        int count = 0;
        try (BufferedReader br = new BufferedReader(new FileReader("user_input.txt"))) {
            while (br.readLine() != null) {
                count++;
            }
            System.out.println("Total lines: " + count);
        } catch (IOException e) {
            System.out.println("Error reading file");
        }
    }
}
        `,
            },
            {
                label: "3. Append text to file",
                code: `
import java.io.FileWriter;
import java.io.IOException;

public class FileAppend {
    public static void main(String[] args) {
        try (FileWriter writer = new FileWriter("user_input.txt", true)) {
            writer.write("\\nAppended line.");
            System.out.println("Text appended.");
        } catch (IOException e) {
            System.out.println("Error appending file");
        }
    }
}
        `,
            },
            {
                label: "4. Delete a file",
                code: `
import java.io.File;

public class FileDelete {
    public static void main(String[] args) {
        File file = new File("user_input.txt");
        if (file.delete()) {
            System.out.println("Deleted: " + file.getName());
        } else {
            System.out.println("Delete failed");
        }
    }
}
        `,
            },
            {
                label: "5. Print unique words from file",
                code: `
import java.io.BufferedReader;
import java.io.FileReader;
import java.io.IOException;
import java.util.HashSet;
import java.util.Set;

public class UniqueWords {
    public static void main(String[] args) {
        Set<String> words = new HashSet<>();
        try (BufferedReader br = new BufferedReader(new FileReader("user_input.txt"))) {
            String line;
            while ((line = br.readLine()) != null) {
                for (String w : line.split("\\\\s+")) {
                    if (!w.isEmpty()) words.add(w);
                }
            }
            System.out.println("Unique words: " + words);
        } catch (IOException e) {
            System.out.println("Error reading file");
        }
    }
}
        `,
            },
        ],
    },

    {
        title: "Java Collections",
        sections: [
            {
                label: "1. Sort names using ArrayList",
                code: `
import java.util.ArrayList;
import java.util.Collections;
import java.util.List;

public class SortNames {
    public static void main(String[] args) {
        List<String> names = new ArrayList<>();
        names.add("Charlie");
        names.add("Alice");
        names.add("Bob");

        Collections.sort(names);
        System.out.println(names);
    }
}
        `,
            },
            {
                label: "2. Remove duplicates using HashSet",
                code: `
import java.util.ArrayList;
import java.util.HashSet;
import java.util.List;
import java.util.Set;

public class RemoveDuplicates {
    public static void main(String[] args) {
        List<Integer> list = new ArrayList<>();
        list.add(10);
        list.add(20);
        list.add(10);

        Set<Integer> set = new HashSet<>(list);
        System.out.println(set);
    }
}
        `,
            },
            {
                label: "3. Word count using HashMap",
                code: `
import java.util.HashMap;
import java.util.Map;

public class WordCount {
    public static void main(String[] args) {
        String text = "hello world hello java";

        Map<String, Integer> map = new HashMap<>();
        for (String word : text.split(" ")) {
            map.put(word, map.getOrDefault(word, 0) + 1);
        }
        System.out.println(map);
    }
}
        `,
            },
            {
                label: "4. Queue using LinkedList",
                code: `
import java.util.LinkedList;
import java.util.Queue;

public class QueueDemo {
    public static void main(String[] args) {
        Queue<String> queue = new LinkedList<>();
        queue.add("Task1");
        queue.add("Task2");
        queue.add("Task3");

        System.out.println(queue.poll());
        System.out.println(queue);
    }
}
        `,
            },
            {
                label: "5. Convert list to set",
                code: `
import java.util.ArrayList;
import java.util.HashSet;
import java.util.List;
import java.util.Set;

public class ListToSet {
    public static void main(String[] args) {
        List<Integer> list = new ArrayList<>();
        list.add(1);
        list.add(2);
        list.add(2);

        Set<Integer> set = new HashSet<>(list);
        System.out.println("List: " + list);
        System.out.println("Set: " + set);
    }
}
        `,
            },
        ],
    },

    {
        title: "Interfaces & Abstract Classes",
        sections: [
            {
                label: "1. Interface implemented by classes",
                code: `
interface Animal {
    void sound();
}

class Cat implements Animal {
    public void sound() {
        System.out.println("Meow");
    }
}

class Dog implements Animal {
    public void sound() {
        System.out.println("Woof");
    }
}

public class InterfaceDemo {
    public static void main(String[] args) {
        Animal a1 = new Cat();
        Animal a2 = new Dog();
        a1.sound();
        a2.sound();
    }
}
        `,
            },
            {
                label: "2. Abstract class with abstract & concrete methods",
                code: `
abstract class Vehicle {
    abstract void start();
    void stop() {
        System.out.println("Vehicle stopped");
    }
}

class Car extends Vehicle {
    void start() {
        System.out.println("Car started");
    }
}

public class AbstractDemo {
    public static void main(String[] args) {
        Vehicle v = new Car();
        v.start();
        v.stop();
    }
}
        `,
            },
            {
                label: "3. Multiple inheritance using interfaces",
                code: `
interface A {
    void methodA();
}

interface B {
    void methodB();
}

class C implements A, B {
    public void methodA() {
        System.out.println("Method A");
    }
    public void methodB() {
        System.out.println("Method B");
    }
}

public class MultipleInheritanceDemo {
    public static void main(String[] args) {
        C obj = new C();
        obj.methodA();
        obj.methodB();
    }
}
        `,
            },
            {
                label: "4. Override abstract methods",
                code: `
abstract class Shape2 {
    abstract void draw();
}

class Triangle extends Shape2 {
    void draw() {
        System.out.println("Drawing triangle");
    }
}

public class AbstractOverrideDemo {
    public static void main(String[] args) {
        Shape2 s = new Triangle();
        s.draw();
    }
}
        `,
            },
            {
                label: "5. Interface vs abstract (example code)",
                code: `
interface Flyable {
    void fly();
}

abstract class Bird {
    abstract void makeSound();
    void sleep() {
        System.out.println("Bird sleeping");
    }
}

class Eagle extends Bird implements Flyable {
    public void fly() {
        System.out.println("Eagle flying");
    }
    void makeSound() {
        System.out.println("Eagle screech");
    }
}

public class InterfaceAbstractComparison {
    public static void main(String[] args) {
        Eagle e = new Eagle();
        e.fly();
        e.makeSound();
        e.sleep();
    }
}
        `,
            },
        ],
    },

    {
        title: "Lambda Expressions",
        sections: [
            {
                label: "1. Print list using lambda",
                code: `
import java.util.Arrays;
import java.util.List;

public class LambdaPrintList {
    public static void main(String[] args) {
        List<String> list = Arrays.asList("Java", "Lambda", "Expressions");
        list.forEach(item -> System.out.println(item));
    }
}
        `,
            },
            {
                label: "2. Sort list using lambda",
                code: `
import java.util.Arrays;
import java.util.List;

public class LambdaSort {
    public static void main(String[] args) {
        List<Integer> nums = Arrays.asList(5, 1, 3, 2, 4);
        nums.sort((a, b) -> a - b);
        System.out.println(nums);
    }
}
        `,
            },
            {
                label: "3. Consumer functional interface",
                code: `
import java.util.function.Consumer;

public class ConsumerDemo {
    public static void main(String[] args) {
        Consumer<String> printer = s -> System.out.println("Message: " + s);
        printer.accept("Lambda is powerful");
    }
}
        `,
            },
            {
                label: "4. Method reference",
                code: `
import java.util.Arrays;
import java.util.List;

public class MethodReferenceDemo {
    public static void main(String[] args) {
        List<String> words = Arrays.asList("Java", "Lambda", "Tutorial");
        words.forEach(System.out::println);
    }
}
        `,
            },
            {
                label: "5. Filter even numbers using lambda",
                code: `
import java.util.Arrays;
import java.util.List;
import java.util.stream.Collectors;

public class LambdaFilterEven {
    public static void main(String[] args) {
        List<Integer> nums = Arrays.asList(1,2,3,4,5,6,7,8);
        List<Integer> evens = nums.stream()
                                  .filter(n -> n % 2 == 0)
                                  .collect(Collectors.toList());
        System.out.println(evens);
    }
}
        `,
            },
        ],
    },

    {
        title: "Multithreading",
        sections: [
            {
                label: "1. Thread using Thread class",
                code: `
class MyThread extends Thread {
    public void run() {
        System.out.println("Thread is running: " + Thread.currentThread().getName());
    }
}

public class ThreadExample {
    public static void main(String[] args) {
        MyThread t1 = new MyThread();
        t1.start();
    }
}
        `,
            },
            {
                label: "2. Thread using Runnable",
                code: `
class MyRunnable implements Runnable {
    public void run() {
        System.out.println("Runnable thread: " + Thread.currentThread().getName());
    }
}

public class RunnableExample {
    public static void main(String[] args) {
        Thread t = new Thread(new MyRunnable());
        t.start();
    }
}
        `,
            },
            {
                label: "3. Synchronize shared counter",
                code: `
class Counter {
    private int count = 0;

    public synchronized void increment() {
        count++;
    }

    public int getCount() {
        return count;
    }
}

public class SyncDemo {
    public static void main(String[] args) throws InterruptedException {
        Counter counter = new Counter();

        Runnable task = () -> {
            for (int i = 0; i < 1000; i++) {
                counter.increment();
            }
        };

        Thread t1 = new Thread(task);
        Thread t2 = new Thread(task);

        t1.start();
        t2.start();

        t1.join();
        t2.join();

        System.out.println("Final count: " + counter.getCount());
    }
}
        `,
            },
            {
                label: "4. ExecutorService run tasks",
                code: `
import java.util.concurrent.ExecutorService;
import java.util.concurrent.Executors;

public class ExecutorDemo {
    public static void main(String[] args) {
        ExecutorService executor = Executors.newFixedThreadPool(3);

        Runnable task = () -> {
            System.out.println("Task running in: " + Thread.currentThread().getName());
        };

        for (int i = 0; i < 5; i++) {
            executor.execute(task);
        }

        executor.shutdown();
    }
}
        `,
            },
            {
                label: "5. Race condition explanation (with fix)",
                code: `
class UnsafeCounter {
    public int count = 0;
}

public class RaceConditionDemo {
    public static void main(String[] args) throws InterruptedException {
        UnsafeCounter c = new UnsafeCounter();

        Runnable task = () -> {
            for (int i = 0; i < 1000; i++) {
                c.count++; // not synchronized -> race condition
            }
        };

        Thread t1 = new Thread(task);
        Thread t2 = new Thread(task);

        t1.start();
        t2.start();
        t1.join();
        t2.join();

        System.out.println("Final count (race condition): " + c.count);
    }
}
        `,
            },
        ],
    },
];

/* ✅ MAIN COMPONENT */
export default function JavaAssignmentSolutions() {
    const [selectedTab, setSelectedTab] = useState(0);

    return (
        <PageWrapper>
            {/* Title */}
            <Box sx={{ textAlign: "center", pt: 10, pb: 3 }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        display: "inline-block",
                        px: 4,
                        py: 1,
                        borderRadius: "999px",
                    }}
                >
                    Java Master Assignment – Full Solutions
                </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
                <TabWrapper>
                    <Tabs
                        value={selectedTab}
                        onChange={(_, v) => setSelectedTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        textColor="inherit"
                        TabIndicatorProps={{ style: { backgroundColor: "goldenrod" } }}
                    >
                        {topics.map((t, i) => (
                            <Tab
                                key={i}
                                label={t.title}
                                sx={{
                                    color: "white",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    "&.Mui-selected": { color: "goldenrod" },
                                }}
                            />
                        ))}
                    </Tabs>
                </TabWrapper>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    {/* Current Topic */}
                    {topics[selectedTab].sections.map((section, index) => (
                        <React.Fragment key={index}>
                            <SectionTitle>
                                <Avatar
                                    sx={{
                                        bgcolor: "lightyellow",
                                        color: "goldenrod",
                                        width: 70,
                                        height: 70,
                                    }}
                                >
                                    <AiFillBulb size={40} />
                                </Avatar>
                                <Typography variant="h6" fontWeight={700} color="white">
                                    {section.label}
                                </Typography>
                            </SectionTitle>

                            <TopicCard>
                                <CodeBlock>{section.code}</CodeBlock>
                            </TopicCard>
                        </React.Fragment>
                    ))}

                    {/* PDF + Back */}
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                        <DownloadPDFButton />
                    </Box>
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                            <BackButton>Back</BackButton>
                        </Link>
                    </Box>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
